import { ReactElement } from "react";
import Head from "next/head";
import { Site } from "@/types/sites";
import { DEFAULT_SITE_PRIMARY_TITLE } from "src/constants";
import { useFlashingTitle } from "src/hooks/useFlashingTitle";
import SeoFields from "../SeoFields/index";
import SeoTitleDesc from "../SeoTitleDesc/index";
import { OfferPageTemplateResponse } from "@/types/offers";
import { LandingPageTemplateResponse } from "@/types/landing";
import useFeatureFlag from "../../../hooks/useFeatureFlag";
import { setCookie } from "../../../utils/cookies/client";
import {
    getSiteBackgrounds,
    getLandingPageBackgrounds,
    getOfferPageBackgrounds,
    type MediaSrcAndMime,
} from "../../../utils/theme/utils"; // avoid import error by reaching into the theme module and importing only the utils we need
import { OneSignalWrapper } from "../ThirdParty/OneSignal";
import { Convert } from "../ThirdParty/Convert";
import { PageData } from "@/types/nextjs";

// !! CAUTION !!
// <Head> is being deprecated, but the new replacement doesn't support custom <style> tags.
// As soon as it does, we should start to migrate to it.
// Currently using <Head> is our only option.
// https://beta.nextjs.org/docs/api-reference/file-conventions/head#supported-head-tags
// !! CAUTION !!

type QihHead = ({
    site,
    siteTheme,
    pageData,
}: {
    site: Site | null;
    siteTheme: string;
    pageData: PageData;
}) => ReactElement;

export const QihHead: QihHead = ({ site, siteTheme, pageData }) => {
    const offerPage = pageData?.offerPage;
    const landingPage = pageData?.landingPage;
    const pageTheme = pageData?.pageTheme;

    const primaryTitle =
        site?.attributes.primaryTitle ??
        site?.attributes?.name ??
        DEFAULT_SITE_PRIMARY_TITLE;
    const currentTitle = useFlashingTitle(
        primaryTitle,
        site?.attributes?.secondaryTitle
    );

    const handleSeoFields = (
        page: OfferPageTemplateResponse | LandingPageTemplateResponse,
        currentTitle: string
    ) => {
        const seoFields = SeoFields(
            page?.data?.attributes?.seoLinkField ?? [],
            page?.data?.attributes?.seoMetaField ?? []
        );
        let seoTitleDescFields = null;
        if (page?.data?.attributes.seo_title_description) {
            seoTitleDescFields = SeoTitleDesc(
                currentTitle,
                page?.data?.attributes.seo_title_description
            );
        }
        return { seoFields, seoTitleDescFields };
    };

    let seoFields = SeoFields(
        site?.attributes?.seoLinkField ?? [],
        site?.attributes?.seoMetaField ?? []
    );
    let seoTitleDescFields = SeoTitleDesc(
        currentTitle,
        site?.attributes?.seo_title_description
    );

    const largestContentfulPaintMitigations: MediaSrcAndMime[] = [];
    const largestContentfulPaintSrcs: Set<string> = new Set();
    const largestContentfulPaintAdder = (bg: MediaSrcAndMime) => {
        const isValid = Boolean(bg.src && bg.mime && bg.src !== "none");
        if (isValid && bg.preload && !largestContentfulPaintSrcs.has(bg.src)) {
            largestContentfulPaintSrcs.add(bg.src);
            largestContentfulPaintMitigations.push(bg);
        }
    };

    // We normally set the background images inside of CSS and so by default
    // the function returns the URLs of the images wrapped in the CSS url() function.
    // However, in this case we are preloading the images inside a <link rel="preload" />
    // tag and so we only want the URLs of the images.
    const urlsOnly = true;

    if (site) {
        getSiteBackgrounds(site, urlsOnly).forEach(largestContentfulPaintAdder);
    }

    if (offerPage) {
        const {
            seoFields: offerPageSeoFields,
            seoTitleDescFields: offerPageSeoTitleDescFields,
        } = handleSeoFields(offerPage, currentTitle);
        seoFields = offerPageSeoFields ?? seoFields;
        seoTitleDescFields = offerPageSeoTitleDescFields ?? seoTitleDescFields;

        getOfferPageBackgrounds(offerPage, urlsOnly).forEach(
            largestContentfulPaintAdder
        );
    } else if (landingPage) {
        const {
            seoFields: landingPageSeoFields,
            seoTitleDescFields: landingPageSeoTitleDescFields,
        } = handleSeoFields(landingPage, currentTitle);
        seoFields = landingPageSeoFields ?? seoFields;
        seoTitleDescFields =
            landingPageSeoTitleDescFields ?? seoTitleDescFields;

        getLandingPageBackgrounds(landingPage, urlsOnly).forEach(
            largestContentfulPaintAdder
        );
    }

    const setFacebookCookies = useFeatureFlag("setFacebookCookies");
    // Facebook cookies are currently only set when on strikewild. This is due to the configuration
    // of the Facebook app. We can use this feature flag to test the cookies on other sites.
    if (setFacebookCookies) {
        setCookie("_fbp", "fb.1.1596403881668.1116446470", 300000);
        setCookie(
            "_fbc",
            "fb.1.1554763741205.AbCdEfGhIjKlMnOpQrStUvWxYz1234567890",
            300000
        );
    }

    return (
        <>
            <Head>
                {seoTitleDescFields}

                {seoFields}

                {offerPage?.data?.attributes?.noIndex ||
                landingPage?.data?.attributes?.noIndex ? (
                    <meta name="robots" content="noindex,nofollow" />
                ) : null}

                {site?.attributes?.ogImage?.data?.attributes?.url ? (
                    <meta
                        property="og:image"
                        content={
                            site?.attributes?.ogImage?.data?.attributes?.url
                        }
                    />
                ) : null}

                {largestContentfulPaintMitigations.map(({ src, mime }) => (
                    <link
                        key={src}
                        rel="preload"
                        href={src}
                        as="image"
                        type={mime}
                        // crossOrigin="anonymous" // This is causing a CORS error with google cloud storage
                    ></link>
                ))}

                {/* Theme was sanitised on the server before being passed in, as long as it hasn't been
            tampered with between the sanitisation and here then this should be perfectly safe */}
                <style key="siteTheme">{siteTheme}</style>

                {pageTheme && <style key="pageTheme">{pageTheme}</style>}
            </Head>

            {/* https://nextjs.org/docs/messages/no-script-component-in-head
                This script was originally implemented in the head tag.
                NextJS has deprecated the use of the <Script> component in the head tag.

            */}
            <Convert site={site} />
            <OneSignalWrapper site={site} hasOfferList={!!offerPage} />
        </>
    );
};
